/**
 * Boots the session (GET /api/me) and decides what the user sees: the welcome
 * page when nobody is logged in, a "geen toegang" page for a session without a
 * TransectLibre role, and otherwise the app shell with the routes.
 *
 * Sessions end on the server (12 h after login, or when an admin revokes the
 * access). A 401 on any request is double-checked with GET /api/me; when the
 * session is really gone, everything of it is cleared and the browser goes to
 * the Microsoft login, which brings the user back to the same page.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import type { MeDto } from '../shared/api.js';
import { NoAccessPage } from './auth/NoAccessPage.js';
import { WelcomePage } from './auth/WelcomePage.js';
import { ErrorState } from './components/States.js';
import { onUnauthorized } from './lib/api.js';
import { browser, claimAutoLogin, loginUrl } from './lib/navigation.js';
import { getMe, hasRole, loadMe, recheckSession, setMe, useMe } from './lib/session.js';
import { clearToasts } from './lib/toast.js';
import { AppRoutes } from './routes.js';
import { AppShell } from './shell/AppShell.js';
import { Splash } from './shell/Splash.js';
import { planningStore } from './store/planningStore.js';

type Boot = { state: 'loading' } | { state: 'ready' } | { state: 'error'; error: unknown };
type Ended = { redirecting: boolean } | null;

export function AppRoot() {
  const me = useMe();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [boot, setBoot] = useState<Boot>({ state: 'loading' });
  const [ended, setEnded] = useState<Ended>(null);
  const loggedIn = me !== null;
  const wasLoggedIn = useRef(false);
  const path = useRef('/');
  path.current = `${location.pathname}${location.search}`;

  const start = useCallback(async () => {
    setBoot({ state: 'loading' });
    try {
      await loadMe();
      setBoot({ state: 'ready' });
    } catch (error) {
      setBoot({ state: 'error', error });
    }
  }, []);

  useEffect(() => {
    let checking = false;
    const sessionEnded = (previous: MeDto) => {
      // The development login has no Microsoft round trip to send the user to.
      const redirect = !previous.devAuth && claimAutoLogin();
      setEnded({ redirecting: redirect });
      setMe(null);
      clearToasts();
      if (redirect) browser.assign(loginUrl(path.current));
    };
    onUnauthorized(() => {
      const previous = getMe();
      // Not logged in yet (boot), or this tab is already checking (the check itself may answer 401).
      if (!previous || checking) return;
      checking = true;
      recheckSession()
        .then((current) => {
          // Unless the user logged out (or in again) meanwhile.
          if (current === null && getMe() === previous) sessionEnded(previous);
        })
        .catch(() => undefined)
        .finally(() => {
          checking = false;
        });
    });
    void start();
    return () => onUnauthorized(null);
  }, [start]);

  // Nothing of a previous session may stay visible after logging out or expiry.
  useEffect(() => {
    if (loggedIn) {
      wasLoggedIn.current = true;
      setEnded(null);
    } else if (wasLoggedIn.current) {
      wasLoggedIn.current = false;
      planningStore.reset();
      queryClient.clear();
    }
  }, [loggedIn, queryClient]);

  if (boot.state === 'loading') return <Splash />;
  if (boot.state === 'error') {
    return (
      <Splash>
        <ErrorState title="TransectLibre kon niet starten" error={boot.error} onRetry={() => void start()} />
      </Splash>
    );
  }
  if (!me) return <WelcomePage expired={ended !== null} redirecting={ended?.redirecting ?? false} />;
  if (!hasRole(me, 'viewer')) return <NoAccessPage me={me} />;
  return (
    <AppShell>
      <AppRoutes />
    </AppShell>
  );
}
